import { createModel, RematchDispatch } from '@rematch/core';
import produce from 'immer';
import { message } from 'antd';

import { RootModel } from '../models';
import { PaginationType } from '../../types/CommonTypes';
import { storeName as tourEntityStoreName } from '../entities/tour.entity';
import { storeName as sceneEntityStoreName } from '../entities/scene.entity';
import { storeName as tourModelStoreName } from './tour.model';
import { DEFAULT_PAGE_SIZE } from '../../commons/constants/constants';
import { TourData } from '../../types/TourData';

export const storeName = 'tourModify';

const initialPaginationValue: PaginationType = {
  pageable: null,
  sort: null,
  totalPages: 0,
  totalElements: 0,
  last: false,
  first: true,
  numberOfElements: 0,
  size: DEFAULT_PAGE_SIZE,
  number: 0,
  empty: null,
};

export interface initialValuesType {
  id: null;
  selectedSceneIds: number[];
  selectedScenePagination: PaginationType | null;
  unselectedSceneIds: number[];
  unselectedScenePagination: PaginationType | null;
  removeSceneIds: number[];
  newSceneIds: number[];
}

export const initialValues: initialValuesType = {
  id: null,
  selectedSceneIds: [],
  selectedScenePagination: initialPaginationValue,
  unselectedSceneIds: [],
  unselectedScenePagination: initialPaginationValue,
  removeSceneIds: [],
  newSceneIds: [],
};

const tourModify: any = createModel<RootModel>()({
  state: initialValues, // initial state
  reducers: {
    reset: () => initialValues,
    setId: produce((state, payload) => {
      state.id = payload;
    }),
    setSelectedSceneIds: produce((state, payload) => {
      state.selectedSceneIds = payload;
    }),
    setUnselectedSceneData: produce((state, { ids, pagination }) => {
      state.unselectedSceneIds = ids || [];
      state.unselectedScenePagination = pagination || initialPaginationValue;
    }),
    setSelectedSceneData: produce((state, { ids, pagination }) => {
      state.selectedSceneIds = ids || [];
      state.selectedScenePagination = pagination || initialPaginationValue;
    }),
    addNewScene: produce((state, id) => {
      const { newSceneIds, selectedSceneIds, removeSceneIds } = state;
      if (selectedSceneIds.includes(id) || newSceneIds.includes(id)) {
        // do nothing
      } else if (removeSceneIds.includes(id)) {
        state.removeSceneIds = removeSceneIds.filter((i) => i !== id);
        state.selectedSceneIds.push(id);
      } else {
        newSceneIds.push(id);
      }
    }),
    removeScene: produce((state, id) => {
      const { newSceneIds, selectedSceneIds } = state;
      let idx = newSceneIds.indexOf(id);
      if (idx >= 0) {
        state.newSceneIds.splice(idx, 1);
      }
      idx = selectedSceneIds.indexOf(id);
      if (idx >= 0) {
        state.selectedSceneIds.splice(idx, 1);
        state.removeSceneIds.push(id);
      }
    }),
  },
  effects: (dispatch: RematchDispatch<typeof tourModify>) => ({
    async editTour(id, state) {
      if (!id) {
        this.reset();
      } else {
        this.setId(id);
        const tourData: TourData = await dispatch[tourEntityStoreName].loadById(id);
        let sceneIds = [];
        if (tourData) {
          sceneIds = tourData.scenes.map((scene) => scene.id);
          dispatch[sceneEntityStoreName].load({ ids: sceneIds, size: sceneIds.length });
        }

        this.setSelectedSceneData({ ids: sceneIds });
      }
    },
    async loadUnselectedScenes(params, state) {
      const data = await dispatch[sceneEntityStoreName].load(params);
      const ids = data.content.map((scene) => scene.id);
      const pagination = (({ content, ...otherProps }) => otherProps)(data);
      this.setUnselectedSceneData({ ids, pagination });
    },
    async save(payload, state) {
      const { id, selectedSceneIds, newSceneIds } = state[storeName];

      const tour = state[tourEntityStoreName].ids[id];

      const sceneEntities = state[sceneEntityStoreName].ids;

      let scenes = [];
      if (tour) {
        const tourScenes = (tour.scenes || []).reduce((rs, scene) => {
          rs[scene.id] = scene;
          return rs;
        }, {});
        // Keep tour scene config (category, POI,...)
        scenes = selectedSceneIds.map((sceneId) => tourScenes[sceneId]);
      }
      const newScenes = newSceneIds.map((sceneId) => sceneEntities[sceneId]);

      const { name: tourName, alias, tags, splashScreen, splashScreenLogo, tourLogo, language, translations } = payload;
      if (scenes.length + newScenes.length > 0 && tourName) {
        await dispatch[tourModelStoreName].saveAsync({
          ...tour,
          alias,
          tags,
          splashScreen,
          splashScreenLogo,
          tourLogo,
          name: tourName || tour.name,
          scenes: [...scenes, ...newScenes],
          language,
          translations,
        });
      }
    },
    async publishAsync(payload, state) {
      try {
        const item = await dispatch[tourEntityStoreName].publish(payload);
        this.setId(item.id);
        message.success(`Success to ${payload.published ? 'published' : 'unpublished'}`);
      } catch (e) {
        console.error(e);
        message.error(`Failed to to ${payload.published ? 'published' : 'unpublished'}`);
      }
    },
  }),
});

export default tourModify;
